import {
  callbackObject,
  getEnableNewStatusMatchApi,
  getIsNewProfilingDataEnabled,
  parseBooleanConfigValue,
} from '@msc-b2c/shared-utils';
import { ConsentCallMeBack } from '@msc-b2c/interfaces/types';
import {
  ContactRequestRequest,
  NewsletterSubscriptionsRequest,
  StatusMatchesRequest,
} from '@msc-b2c/interfaces/interfaces';
import intlTelInput from 'intl-tel-input';

const DEFAULT_CONSENT_SOURCE = 'Webform';
const CONTACT_US_WITH_BKD_SOURCE = 'Contact us_With bkd';
const CONTACT_US_CMB_SOURCE = 'Contact us_CMB';
const NEWSLETTER_SOURCE = 'Newsletter';
const STATUS_MATCH_SOURCE = 'Status Match';
const HOLD_OPTION_SOURCE = 'B2C Hold Option';
const REQUEST_QUOTATION_SOURCE = 'Request_Quotation';
const CONSENT_CHANNELS = ['email', 'phone', 'mobilePhone'] as const;
const CONTACT_US_WEBFORM_CODES = new Set(['contactus', 'contactus_de', 'contactus_fr']);

let isEnableTheNewStatusMatchApi = false;

const getConsentSource = (serializedForm: ConsentCallMeBack): string => {
  const action = String(serializedForm['action'] ?? '')
    .trim()
    .toLowerCase();
  const webformCodeValue = String(serializedForm['webformCode'] ?? '').trim();
  const customWebformCodeValue = String(serializedForm['customWebformCode_skip'] ?? '').trim();
  const effectiveWebformCode =
    webformCodeValue.toLowerCase() === 'customvalues' && customWebformCodeValue
      ? customWebformCodeValue
      : webformCodeValue;

  const webformCode = effectiveWebformCode.toLowerCase();
  const campaignName = String(serializedForm['campaignName'] ?? '').toLowerCase();

  if (action === 'subscriptions' || webformCode === 'newslettersignup') {
    return NEWSLETTER_SOURCE;
  }

  if (
    action === 'status-matches' ||
    webformCode.includes('statusmatch') ||
    webformCode === 'callmeback'
  ) {
    return STATUS_MATCH_SOURCE;
  }

  if (webformCode.includes('hold') || campaignName.includes('hold option')) {
    return HOLD_OPTION_SOURCE;
  }

  if (campaignName.includes('request for quotation') || webformCode.includes('r4q')) {
    return REQUEST_QUOTATION_SOURCE;
  }

  if (CONTACT_US_WEBFORM_CODES.has(webformCode) || webformCode.startsWith('contactus')) {
    return CONTACT_US_WITH_BKD_SOURCE;
  }

  if (webformCode.startsWith('cmb')) {
    return CONTACT_US_CMB_SOURCE;
  }

  return DEFAULT_CONSENT_SOURCE;
};

const normalizeProfilingConsents = (
  serializedForm: ConsentCallMeBack
): Record<string, any> | undefined => {
  const rawProfilingConsents = serializedForm['profilingConsents'];
  const rawProfilingConsent = serializedForm['profilingConsent'];

  if (
    rawProfilingConsents &&
    typeof rawProfilingConsents === 'object' &&
    !Array.isArray(rawProfilingConsents)
  ) {
    const normalizedProfilingConsents: Record<string, any> = { ...rawProfilingConsents };
    let hasChannelValues = false;
    let hasOptInChannel = false;

    CONSENT_CHANNELS.forEach((channel) => {
      const parsedValue = parseBooleanConfigValue(normalizedProfilingConsents[channel]);
      if (typeof parsedValue === 'boolean') {
        normalizedProfilingConsents[channel] = parsedValue;
        hasChannelValues = true;
        if (parsedValue) {
          hasOptInChannel = true;
        }
      }
    });

    if (hasChannelValues && hasOptInChannel) {
      return normalizedProfilingConsents;
    }

    return undefined;
  }

  const parsedProfilingConsent =
    parseBooleanConfigValue(rawProfilingConsents) ?? parseBooleanConfigValue(rawProfilingConsent);
  if (parsedProfilingConsent === true) {
    const normalizedProfilingConsents: Record<string, boolean> = {};
    CONSENT_CHANNELS.forEach((channel) => {
      normalizedProfilingConsents[channel] = !!serializedForm.customer?.[channel];
    });
    return normalizedProfilingConsents;
  }

  return undefined;
};

const addConsentMetadata = (
  consents: Record<string, any>,
  acquisitionDateTime: string,
  consentSource: string
): void => {
  CONSENT_CHANNELS.forEach((channel) => {
    if (typeof consents[channel] === 'boolean') {
      consents[`${channel}Source`] = consentSource;
      consents[`${channel}AcquisitionDateTime`] = acquisitionDateTime;
    }
  });
};

const removeConsentMetadata = (consents: Record<string, any>): void => {
  CONSENT_CHANNELS.forEach((channel) => {
    delete consents[`${channel}Source`];
    delete consents[`${channel}AcquisitionDateTime`];
  });
};

const populateAbsoluteConfirmationPage = (formData: Record<string, any>): void => {
  if (typeof formData['confirmationPage'] !== 'string') {
    return;
  }

  const confirmationPage = formData['confirmationPage'].trim();
  if (!confirmationPage) {
    return;
  }

  const sourcePage =
    typeof formData['sourcePage'] === 'string' && formData['sourcePage'].trim()
      ? formData['sourcePage']
      : window.location.href;

  try {
    formData['confirmationPage'] = new URL(confirmationPage, sourcePage).toString();
  } catch {
    formData['confirmationPage'] = confirmationPage;
  }
};

/**
 * Sets a deep value in an object based on a dot-separated path.
 *
 * @param {Record<string, string | number | boolean>} obj - The object in which to set the value.
 * @param {string} path - The dot-separated path indicating where to set the value.
 * @param {FormDataEntryValue} value - The value to set at the specified path.
 * @param formElement
 */
const setDeepValue = (
  obj: Record<string, any>,
  path: string,
  value: FormDataEntryValue,
  formElement: HTMLFormElement
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const keys = path.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
        .replace(':boolean', '')
        .replace(':tel', '')
        .replace(':number', '')
        .replace(':file', '');
      if (!current[key]) {
        current[key] = {} as Record<string, any>;
      }
      current = current[key] as unknown as Record<string, any>;
    }

    const finalKey = keys[keys.length - 1];
    const isBooleanKey = finalKey.endsWith(':boolean');
    const isTelKey = finalKey.endsWith(':tel');
    const isNumberKey = finalKey.endsWith(':number');
    const isFileKey = finalKey.endsWith(':file');
    const cleanKey = finalKey
      .replace(':boolean', '')
      .replace(':tel', '')
      .replace(':number', '')
      .replace(':file', '');

    if (value !== '') {
      if (isBooleanKey) {
        current[cleanKey] = value === 'true' || value === '1';
        resolve();
      } else if (isTelKey) {
        const inputElement = formElement.querySelector(`input[name="${path}"]`) as HTMLInputElement;
        current[cleanKey] = intlTelInput.getInstance(inputElement)?.getNumber() ?? value;
        resolve();
      } else if (isNumberKey && typeof value === 'string') {
        current[cleanKey] = parseInt(value, 10);
        resolve();
      } else if (isFileKey && value instanceof File) {
        if (value.name !== '') {
          const reader = new FileReader();
          reader.onload = function (event) {
            const base64Data = event.target?.result as string;
            const cleanBase64 = base64Data.split(',')[1];
            current[cleanKey] = {
              name: value.name,
              data: cleanBase64,
            };
            resolve();
          };
          reader.onerror = reject;
          reader.readAsDataURL(value);
        } else {
          resolve();
        }
      } else {
        current[finalKey] = value;
        resolve();
      }
    } else {
      resolve();
    }
  });
};

/**
 * Serializes the form data into a JSON object.
 *
 * @param {HTMLFormElement} form - The form element to be serialized.
 * @returns {Record<string, string | number | boolean>} - The serialized form data as a JSON object.
 */
const serializeForm = async (
  form: HTMLFormElement
): Promise<Record<string, string | number | boolean>> => {
  populateSourceCaseId(form);

  const formData = new FormData(form);
  const json: Record<string, string | number | boolean> = {};
  const promises: Promise<void>[] = [];
  formData.forEach((value, key) => {
    let valueTrimmed = value;
    if (key === 'customer.surname' || key === 'customer.name') {
      valueTrimmed = String(value).trim();
    }
    promises.push(setDeepValue(json, key, valueTrimmed, form));
  });

  const addCheckboxValueIfNotPresent = (form: HTMLFormElement, checkboxName: string) => {
    const checkbox = form.querySelector(
      `input[type="checkbox"][name="${checkboxName}"], input[type="hidden"][name="${checkboxName}"]`
    );
    const hasCheckbox = checkbox !== null;
    if (!formData.has(checkboxName) && hasCheckbox) {
      promises.push(setDeepValue(json, checkboxName, 'false', form));
      checkbox?.setAttribute('tabindex', '0');
    }
  };

  addCheckboxValueIfNotPresent(form, 'genericMarketingConsent_skip:boolean');

  const profilingCheckboxNames = Array.from(
    form.querySelectorAll<HTMLInputElement>(
      'input[type="checkbox"][name], input[type="hidden"][name]'
    )
  )
    .map((checkbox) => checkbox.name)
    .filter((name) => name.toLowerCase().includes('profilingconsent'));

  [...new Set(profilingCheckboxNames)].forEach((checkboxName) => {
    addCheckboxValueIfNotPresent(form, checkboxName);
  });

  await Promise.all(promises);
  return json;
};

/**
 * Recursively removes fields ending with '_skip' from the given object.
 *
 * @param {Record<string, any>} obj - The object from which to remove fields.
 * @returns {Record<string, any>} - The cleaned object with '_skip' fields removed.
 */
const removeSkipFields = (obj: Record<string, any>): ContactRequestRequest => {
  const cleanedObj: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    if (!key.endsWith('_skip')) {
      const value = obj[key];
      cleanedObj[key] =
        typeof value === 'object' && value !== null && !Array.isArray(value)
          ? removeSkipFields(value)
          : value;
      if (key === 'sourcePage') {
        cleanedObj[key] = window.location.href;
      }
    }
  });

  return cleanedObj as ContactRequestRequest;
};

/**
 * Populates the marketing consent fields in the serialized form data.
 * base on the fields filled by the user if the genericMarketingConsent is true then the specific marketing consents is populated
 *
 * @param {ConsentCallMeBack} serializedForm - The serialized form data to be processed.
 */
const populateConsentFields = (serializedForm: ConsentCallMeBack): void => {
  if (serializedForm) {
    const isNewProfilingDataEnabled = getIsNewProfilingDataEnabled();
    const acquisitionDateTime = new Date().toISOString();
    const consentSource = getConsentSource(serializedForm);
    const parsedGenericMarketingConsent = parseBooleanConfigValue(
      serializedForm['genericMarketingConsent_skip']
    );
    const hasMarketingConsentsObject =
      typeof serializedForm.marketingConsents === 'object' &&
      serializedForm.marketingConsents !== null &&
      !Array.isArray(serializedForm.marketingConsents);
    const shouldFallbackToNewsletterMarketingConsent =
      consentSource === NEWSLETTER_SOURCE &&
      typeof parsedGenericMarketingConsent !== 'boolean' &&
      !hasMarketingConsentsObject;
    const effectiveMarketingConsent =
      typeof parsedGenericMarketingConsent === 'boolean'
        ? parsedGenericMarketingConsent
        : shouldFallbackToNewsletterMarketingConsent
        ? true
        : undefined;

    if (
      effectiveMarketingConsent === true ||
      effectiveMarketingConsent === false ||
      hasMarketingConsentsObject
    ) {
      serializedForm.marketingConsents = serializedForm.marketingConsents || {};
      const marketingConsents = serializedForm.marketingConsents as Record<string, any>;

      if (effectiveMarketingConsent === true) {
        marketingConsents['email'] = !!serializedForm.customer?.email;
        marketingConsents['phone'] = !!serializedForm.customer?.phone;
        marketingConsents['mobilePhone'] = !!serializedForm.customer?.mobilePhone;
      } else if (effectiveMarketingConsent === false) {
        marketingConsents['email'] = false;
        marketingConsents['phone'] = false;
        marketingConsents['mobilePhone'] = false;
      }

      if (isNewProfilingDataEnabled) {
        addConsentMetadata(marketingConsents, acquisitionDateTime, consentSource);
      } else {
        removeConsentMetadata(marketingConsents);
      }
    }

    if (isNewProfilingDataEnabled && typeof serializedForm['privacyConsent'] === 'boolean') {
      serializedForm['privacyConsentSource'] = consentSource;
      serializedForm['privacyConsentAcquisitionDateTime'] = acquisitionDateTime;
    } else {
      delete serializedForm['privacyConsentSource'];
      delete serializedForm['privacyConsentAcquisitionDateTime'];
    }

    const normalizedProfilingConsents = normalizeProfilingConsents(serializedForm);
    delete serializedForm['profilingConsent'];

    if (isNewProfilingDataEnabled && normalizedProfilingConsents) {
      const profilingConsents = normalizedProfilingConsents;
      serializedForm['profilingConsents'] = profilingConsents;
      addConsentMetadata(profilingConsents, acquisitionDateTime, consentSource);
    } else {
      delete serializedForm['profilingConsents'];
    }
  }
};

/**
 * Prepares the form request by serializing the form data, populating consent fields,
 * and handling specific actions based on the form data.
 *
 * @param {HTMLFormElement} form - The form element to be serialized and processed.
 * @returns {Record<string, any>} - The processed form data with skip fields removed.
 */
export const prepareFormRequest = async (
  form: HTMLFormElement
): Promise<ContactRequestRequest | StatusMatchesRequest | NewsletterSubscriptionsRequest> => {
  const formData: Record<string, any> = await serializeForm(form);

  populateConsentFields(formData);
  populateCampaignDetails(formData);

  if (formData['action'] === 'contact-requests' || formData['action'] === 'status-matches') {
    isEnableTheNewStatusMatchApi = getEnableNewStatusMatchApi();
    formData['submissionDateTime'] = Math.floor(Date.now() / 1000);
    if (isEnableTheNewStatusMatchApi) {
      matchAPICountryCode(formData);
    }
  }

  if (formData['action'] === 'contact-requests' && formData['callback']) {
    if (formData['callback'].asSoonAsPossible) {
      formData['callback'] = { asSoonAsPossible: true };
    } else {
      delete formData['callback'].asSoonAsPossible;
      formData['callback'] = { ...formData['callback'], ...callbackObject() };
    }
  }

  delete formData['action'];
  const cleanedFormData = removeSkipFields(formData) as Record<string, any>;
  populateAbsoluteConfirmationPage(cleanedFormData);
  return cleanedFormData as
    | ContactRequestRequest
    | StatusMatchesRequest
    | NewsletterSubscriptionsRequest;
};

/* TODO: Remove this after mapping of campaign as Environments Variables */
const populateCampaignDetails = (formData: Record<string, any>): void => {
  const isOldWebFormCodeVersion =
    document.querySelector('meta[name="webformCodeVersion"]')?.getAttribute('content') || 'old';
  if (isOldWebFormCodeVersion === 'old') {
    if (formData['webformCode'] === 'customValues') {
      formData['webformCode'] = formData['customWebformCode_skip'];
      return;
    }

    const mapping = [
      {
        webformCode: 'cmbDem',
        campaignCode: '03ac3f81-3f0a-41d3-8d4f-f7d7e6f185b2',
        campaignName: 'Call Me Back DEM',
      },
      {
        webformCode: 'cmbStickyBanner',
        campaignCode: '45117ad2-1b41-4fe6-b0c2-00003fa67d77',
        campaignName: 'Sticky Banner',
      },
      {
        webformCode: 'cmbSocialMedia',
        campaignCode: '9868d376-c7f3-4716-927f-4ef11b6e01ed',
        campaignName: 'Social Media',
      },
      {
        webformCode: 'cmbPromoForm',
        campaignCode: '06635922-f3af-4b27-90ba-a50c45c2802b',
        campaignName: 'Promo1/Promo2',
      },
      {
        webformCode: 'cmbNurturingDem',
        campaignCode: '27b031d3-b0c5-4103-8173-e89b26bd34ae',
        campaignName: 'Nurturing DEM',
      },
      {
        webformCode: 'cmb',
        campaignCode: '40050c92-8996-4f73-ae5d-84757770b182',
        campaignName: 'Call Me Back',
      },
      {
        webformCode: 'cmbLeadsMoving',
        campaignCode: 'c7e829b3-0363-4aae-8476-7cd51d8b240a',
        campaignName: 'Leads Moving',
      },
      {
        webformCode: 'contactUs',
        campaignCode: '0e3897ac-9658-47f4-b84b-f2fee0a7bde4',
        campaignName: 'Contact Us',
      },
      {
        webformCode: 'cmbR4Q',
        campaignCode: '497757ce-e3b5-49be-8fcf-7ffb799e108f',
        campaignName: 'Request for quotation',
      },
      {
        webformCode: 'newsletterSignup',
        campaignCode: 'e173a337-4f59-41f9-bb2c-a76b6a3f80e3',
        campaignName: 'Newsletter Signup',
      },
    ];

    const campaign = mapping.find((m) => m.webformCode === formData['webformCode']);
    if (campaign) {
      formData['campaignCode'] = campaign.campaignCode;
      formData['campaignName'] = campaign.campaignName;
    }
  }
};

const populateSourceCaseId = (form: HTMLFormElement): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const sourceCaseId = urlParams.get('sourceCaseId');
  const isReadingSourceCaseId = form.getAttribute('data-sourcecaseid');

  if (sourceCaseId) {
    if (isReadingSourceCaseId == 'true') {
      const hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = 'sourceCaseId';
      hiddenInput.value = sourceCaseId;

      form.appendChild(hiddenInput);
    }
  }
};

/**
 * Maps standard two-letter country codes (ISO 3166-1 alpha-2) to three-letter codes
 * (ISO 3166-1 alpha-3) for API compatibility.
 *
 * Checks if the `formData` object contains a supported country code in `customer.address.countryCode`
 * (e.g., 'US' or 'PR') and updates it to its corresponding 3-letter code ('USA', 'PRI').
 *
 * @param {any} formData - The payload containing customer and address details.
 * @returns {any} The updated `formData` object with the mapped country code.
 */
//TODO: Remove hardcoded mapping for the country code in the status match
type CountryCodeMap = Record<string, string>;
const COUNTRY_CODE_MAP: CountryCodeMap = {
  US: 'USA',
  PR: 'PRI',
};

function matchAPICountryCode(formData: any): any {
  const currentCode = formData?.customer?.address?.countryCode;
  if (currentCode && COUNTRY_CODE_MAP[currentCode]) {
    formData.customer.address.countryCode = COUNTRY_CODE_MAP[currentCode];
  }
  return formData;
}
